import { useCallback, useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import { SprintReportGroupKey } from '../../../../lib/functions/sprintReport'
import { DayOfWeek, TimeGrain } from '../../../../lib/functions/report'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

export type SprintReportDateTerm = {
  startDate?: number
  endDate?: number
}

export type SprintReportSearchCondition = {
  rootWbsItemUuid?: string
  teamUuid?: string
  groupKey: SprintReportGroupKey
  dateTerm: SprintReportDateTerm
  timeGrain: TimeGrain
  startDayOfWeek: DayOfWeek
  startSprintUuid?: string
  endSprintUuid?: string
}

export type SprintReportSearchConditionApi = {
  initialized: boolean
  searchCondition: SprintReportSearchCondition
  setRootWbsItemUuid: (value: string | undefined) => void
  setTeamUuid: (value: string | undefined) => void
  setGroupKey: (value: SprintReportGroupKey) => void
  setDateTerm: (value: SprintReportDateTerm) => void
  setTimeGrain: (value: TimeGrain) => void
  setStartDayOfWeek: (value: DayOfWeek) => void
  setStartSprintUuid: (value: string | undefined) => void
  setEndSprintUuid: (value: string | undefined) => void
  applySearchCondition: (
    value: Partial<SprintReportSearchCondition> | undefined
  ) => void
}

const getDefaultDateTerm = (): SprintReportDateTerm => {
  const today = dateV2Service.today()
  const nextMonth = dateV2Service.addMonths(today, 1)
  return {
    startDate: dateV2Service.startOfMonth(today).getTime(),
    endDate: dateV2Service.endOfMonth(nextMonth).getTime(),
  }
}

const defaultSearchCondition = (
  initial: Partial<SprintReportSearchCondition>
): SprintReportSearchCondition => ({
  rootWbsItemUuid: initial.rootWbsItemUuid,
  teamUuid: initial.teamUuid,
  groupKey: initial.groupKey ?? SprintReportGroupKey.SCHEDULED_END_DATE,
  dateTerm: initial.dateTerm ?? getDefaultDateTerm(),
  timeGrain: initial.timeGrain ?? TimeGrain.WEEK,
  startDayOfWeek: initial.startDayOfWeek ?? DayOfWeek.MONDAY,
  startSprintUuid: initial.startSprintUuid,
  endSprintUuid: initial.endSprintUuid,
})

// uistate のキーは旧 BulkSheet と同形式で、プロジェクト単位に分ける（チーム / WBS の選択は
// プロジェクトごとに分けたいユーザー期待値のため）
const getStateKey = (projectUuid: string): string =>
  `${UiStateKey.BulkSheetSearchCondisionState}-${projectUuid}`

export const useSearchCondition = (
  functionUuid: string,
  projectUuid: string,
  initial: Partial<SprintReportSearchCondition> = {}
): SprintReportSearchConditionApi => {
  const [searchCondition, setSearchCondition] =
    useState<SprintReportSearchCondition>(() => defaultSearchCondition(initial))
  // 現在の searchCondition がどの projectUuid 用に復元済みかを保持する。
  // useState の `initialized` フラグだけだと、プロジェクト切替直後 (state 反映前) に
  // 「initialized=true / projectUuid=新 / searchCondition=旧」の組合せで save effect が走り、
  // 旧プロジェクトの検索条件で新プロジェクトのキーを上書きする race が発生するため、
  // 「どの project で復元したか」を別 state で持って derived な initialized 値を返す。
  const [restoredFor, setRestoredFor] = useState<string | undefined>(undefined)
  const initialized = !!projectUuid && restoredFor === projectUuid

  // URL クエリ等で渡された initial.rootWbsItemUuid は uistate より優先する
  const initialRootWbsItemUuid = initial.rootWbsItemUuid

  // 初回マウント時とプロジェクト切替時に uistate から復元する。
  // 復元開始時に restoredFor を undefined に戻すことで、復元完了までは initialized=false 扱いになり、
  // 呼び出し側 (auto-fetch / 内部の save effect) を待たせられる。
  useEffect(() => {
    if (!projectUuid) return
    setRestoredFor(undefined)
    const restore = async () => {
      try {
        const response = await uiStates.get({
          applicationFunctionUuid: functionUuid,
          key: getStateKey(projectUuid),
          scope: UiStateScope.User,
        })
        const stored = response.json
        if (stored?.value) {
          const payload: Partial<SprintReportSearchCondition> = JSON.parse(
            stored.value
          )
          setSearchCondition(prev => ({
            ...prev,
            ...payload,
            // URL で root が明示されていればそれを優先
            rootWbsItemUuid: initialRootWbsItemUuid ?? payload.rootWbsItemUuid,
          }))
        }
      } finally {
        setRestoredFor(projectUuid)
      }
    }
    restore()
  }, [functionUuid, projectUuid, initialRootWbsItemUuid])

  // 変更時に uistate に保存。
  // initialized が「現在の projectUuid に対する復元が完了した状態」を表すため、
  // この effect 内で getStateKey(projectUuid) を使うのは安全 (race window では initialized=false で早期 return される)。
  useEffect(() => {
    if (!initialized) return
    uiStates.update(
      {
        key: getStateKey(projectUuid),
        scope: UiStateScope.User,
        value: JSON.stringify(searchCondition),
      },
      functionUuid
    )
  }, [initialized, searchCondition, functionUuid, projectUuid])

  const setRootWbsItemUuid = useCallback((value: string | undefined) => {
    setSearchCondition(prev => ({ ...prev, rootWbsItemUuid: value }))
  }, [])

  const setTeamUuid = useCallback((value: string | undefined) => {
    // チーム変更時はスプリント絞り込みもリセットする (カンバンと同じ挙動)。
    // 旧チームのスプリントが新チームに残ったままだと、選択肢から外れて見えなくなり混乱する。
    setSearchCondition(prev => ({
      ...prev,
      teamUuid: value,
      startSprintUuid: undefined,
      endSprintUuid: undefined,
    }))
  }, [])

  const setGroupKey = useCallback((value: SprintReportGroupKey) => {
    setSearchCondition(prev => {
      // SPRINT 列モードから日付モードに戻すときは、選択中のスプリント範囲をクリアする。
      // 値が残ったままだと、再び SPRINT モードに切り替えたとき古いスプリント選択が
      // 復活して見えたり、キャッシュキーに混入して意図せぬ取得が走るため。
      const switchedFromSprintMode =
        prev.groupKey === SprintReportGroupKey.SPRINT &&
        value !== SprintReportGroupKey.SPRINT
      return {
        ...prev,
        groupKey: value,
        startSprintUuid: switchedFromSprintMode
          ? undefined
          : prev.startSprintUuid,
        endSprintUuid: switchedFromSprintMode ? undefined : prev.endSprintUuid,
      }
    })
  }, [])

  const setDateTerm = useCallback((value: SprintReportDateTerm) => {
    setSearchCondition(prev => ({ ...prev, dateTerm: value }))
  }, [])

  const setTimeGrain = useCallback((value: TimeGrain) => {
    setSearchCondition(prev => ({ ...prev, timeGrain: value }))
  }, [])

  const setStartDayOfWeek = useCallback((value: DayOfWeek) => {
    setSearchCondition(prev => ({ ...prev, startDayOfWeek: value }))
  }, [])

  const setStartSprintUuid = useCallback((value: string | undefined) => {
    setSearchCondition(prev => ({ ...prev, startSprintUuid: value }))
  }, [])

  const setEndSprintUuid = useCallback((value: string | undefined) => {
    setSearchCondition(prev => ({ ...prev, endSprintUuid: value }))
  }, [])

  // 保存済み UI state から復元する用途。
  // 個別 setter で 1 フィールドずつ更新すると後続の auto-save effect が
  // 中間 state を都度永続化してしまうため、まとめて 1 回の setState で適用する。
  const applySearchCondition = useCallback(
    (value: Partial<SprintReportSearchCondition> | undefined) => {
      if (!value) return
      setSearchCondition(prev => ({ ...prev, ...value }))
    },
    []
  )

  return useMemo(
    () => ({
      initialized,
      searchCondition,
      setRootWbsItemUuid,
      setTeamUuid,
      setGroupKey,
      setDateTerm,
      setTimeGrain,
      setStartDayOfWeek,
      setStartSprintUuid,
      setEndSprintUuid,
      applySearchCondition,
    }),
    [
      initialized,
      searchCondition,
      setRootWbsItemUuid,
      setTeamUuid,
      setGroupKey,
      setDateTerm,
      setTimeGrain,
      setStartDayOfWeek,
      setStartSprintUuid,
      setEndSprintUuid,
      applySearchCondition,
    ]
  )
}
